import { ICONS, routeIcon } from './callScreenIcons.js';
import { audioControlOf, describeCall, hueOf, initialsOf, routeLabelOf } from './callScreenModel.js';
import { createTranslator } from './callScreenStrings.js';
import './callScreen.scss';

// A transparent poster: without one Android's WebView draws its own grey
// "play" placeholder over a video that has no frames yet - an audio call.
const POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const LEAVE_MS = 220;
const HINT_MS = 2400;

let nextId = 0;

function el(tag, className, attributes = {}) {
    const node = document.createElement(tag);
    if(className) node.className = className;
    for(const [name, value] of Object.entries(attributes)) {
        if(value !== undefined && value !== null) node.setAttribute(name, value);
    }
    return node;
}

//********************* CALL SCREEN ********************************//
// The full-screen call window - the same one for a call placed from this
// page (SIP.js) and for a call the native engine runs (CallKit on iOS, the
// self-managed Telecom call on Android) once it is answered.
//
// Plain DOM rather than an Angular template: it is opened from SIP.js and
// Capacitor callbacks outside any digest, and it is built once and updated in
// place, so a change of state animates instead of re-rendering. The <video>
// elements are created with it and never hidden with display:none - the
// remote one is also what plays the other person's voice, and a hidden
// element can miss its autoplay.
//
// It knows nothing about SIP or the native plugin: it draws a state object
// and reports button presses through `handlers`. dialogMixin owns both.
export class CallScreen {

    constructor({ handlers = {}, t = createTranslator(), host = null } = {}) {
        this.handlers = handlers;
        this.t = t;
        this.host = host;
        this.state = null;
        this.root = null;
        this._timer = null;
        this._hintTimer = null;
        this._closeTimer = null;
        this._closing = null;
        this._busy = new Set();
        this._previousFocus = null;
        this._onKeyDown = this._onKeyDown.bind(this);
    }

    get isOpen() {
        return Boolean(this.root) && !this._closing;
    }

    get remoteVideo() {
        return this.root?.querySelector('.gh-call__remote-video') || null;
    }

    get localVideo() {
        return this.root?.querySelector('.gh-call__local-video') || null;
    }

    //********************* OPEN ********************************//
    open(state) {
        if(this.root) return this.update(state);

        this.state = { muted: false, video: {}, audio: null, ...state };
        this._build();
        (this.host || document.body).appendChild(this.root);
        document.documentElement.classList.add('gh-call-open');
        document.addEventListener('keydown', this._onKeyDown, true);

        this._previousFocus = document.activeElement;
        this._render();

        // One frame in the entering state, so the first paint can transition.
        this.root.classList.add('gh-call--entering');
        requestAnimationFrame(() => requestAnimationFrame(() => {
            this.root?.classList.remove('gh-call--entering');
        }));
        this._focusPrimary();
        return this;
    }

    //********************* UPDATE ********************************//
    update(patch = {}) {
        if(!this.root || this._closing) return this;
        const previous = this.state;
        this.state = {
            ...previous,
            ...patch,
            video: { ...previous.video, ...(patch.video || {}) },
        };
        if(patch.audio === null) this.state.audio = null;

        // A new phase answers whatever was pressed in the last one.
        if(patch.phase && patch.phase !== previous.phase) {
            this._busy.clear();
            if(patch.phase !== 'ended') {
                clearTimeout(this._closeTimer);
                this._closeTimer = null;
            }
        }
        this._render();
        if(patch.phase && patch.phase !== previous.phase) this._focusPrimary();
        return this;
    }

    //********************* CLOSE ********************************//
    close({ immediate = false } = {}) {
        if(!this.root) return Promise.resolve();
        if(this._closing && !immediate) return this._closing;

        clearInterval(this._timer);
        clearTimeout(this._hintTimer);
        clearTimeout(this._closeTimer);
        document.removeEventListener('keydown', this._onKeyDown, true);

        const root = this.root;
        const finish = () => {
            if(root.isConnected) root.remove();
            if(this.root === root) this.root = null;
            if(!document.querySelector('.gh-call')) document.documentElement.classList.remove('gh-call-open');
            this._restoreFocus();
        };

        if(immediate) {
            finish();
            this._closing = null;
            return Promise.resolve();
        }

        root.classList.add('gh-call--leaving');
        this._closing = new Promise((resolve) => setTimeout(() => {
            finish();
            this._closing = null;
            resolve();
        }, LEAVE_MS));
        return this._closing;
    }

    //********************* HINT ********************************//
    // A short note over the screen - "Video calls are coming soon".
    showHint(text) {
        const hint = this.root?.querySelector('.gh-call__hint');
        if(!hint) return;
        hint.textContent = text;
        hint.classList.add('gh-call__hint--shown');
        clearTimeout(this._hintTimer);
        this._hintTimer = setTimeout(() => hint.classList.remove('gh-call__hint--shown'), HINT_MS);
    }

    //********************* BUILD ********************************//
    _build() {
        const t = this.t;
        const nameId = `gh-call-name-${++nextId}`;
        const root = el('div', 'gh-call', { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': nameId, tabindex: '-1' });

        root.innerHTML = `
            <div class="gh-call__backdrop"></div>
            <video class="gh-call__remote-video" poster="${POSTER}" autoplay playsinline></video>
            <video class="gh-call__local-video" poster="${POSTER}" autoplay playsinline muted></video>
            <div class="gh-call__layout">
                <div class="gh-call__identity">
                    <div class="gh-call__portrait">
                        <span class="gh-call__halo" aria-hidden="true">
                            <span class="gh-call__ring"></span>
                            <span class="gh-call__ring"></span>
                            <span class="gh-call__ring"></span>
                            <span class="gh-call__spinner"></span>
                        </span>
                        <span class="gh-call__avatar" aria-hidden="true">
                            <span class="gh-call__initials"></span>
                            <span class="gh-call__avatar-glyph">${ICONS.person}</span>
                            <img class="gh-call__photo" alt="" hidden>
                        </span>
                        <span class="gh-call__mute-badge" aria-hidden="true">${ICONS.micOff}</span>
                    </div>
                    <h2 class="gh-call__name" id="${nameId}"></h2>
                    <p class="gh-call__status" role="status" aria-live="polite"></p>
                    <p class="gh-call__detail"></p>
                    <div class="gh-call__notice" hidden>
                        <span class="gh-call__notice-dot" aria-hidden="true"></span>
                        <span class="gh-call__notice-text"></span>
                        <button type="button" class="gh-call__notice-action" data-action="settings" hidden>${t('settings')}</button>
                    </div>
                </div>
                <div class="gh-call__dock">
                    <div class="gh-call__controls">
                        ${this._controlHtml('mute', ICONS.mic, t('mute'))}
                        ${this._controlHtml('audio', ICONS.speaker, t('speaker'))}
                        ${this._controlHtml('video', ICONS.videoOff, t('video'), `<span class="gh-call__badge">${t('soon')}</span>`)}
                    </div>
                    <div class="gh-call__actions">
                        ${this._actionHtml('decline', 'gh-call__disc--end', ICONS.hangUp, t('decline'))}
                        ${this._actionHtml('accept', 'gh-call__disc--accept', ICONS.call, t('accept'))}
                        ${this._actionHtml('hangup', 'gh-call__disc--end gh-call__disc--large', ICONS.hangUp, t('hangUp'), true)}
                        ${this._actionHtml('close', 'gh-call__disc--quiet', ICONS.close, t('close'))}
                        ${this._actionHtml('settings', 'gh-call__disc--quiet', ICONS.settings, t('settings'))}
                        ${this._actionHtml('retry', 'gh-call__disc--accept', ICONS.call, t('callAgain'))}
                    </div>
                </div>
            </div>
            <div class="gh-call__sheet-layer" hidden>
                <div class="gh-call__scrim" data-action="sheet-close"></div>
                <div class="gh-call__sheet" role="menu" aria-label="${t('audioOutput')}">
                    <p class="gh-call__sheet-title">${t('audioOutput')}</p>
                    <div class="gh-call__sheet-list"></div>
                </div>
            </div>
            <div class="gh-call__hint" role="status" aria-live="polite"></div>
        `;

        root.addEventListener('click', (event) => this._onClick(event));
        root.querySelector('.gh-call__photo').addEventListener('error', (event) => {
            event.currentTarget.hidden = true;
            this.root?.classList.remove('gh-call--photo');
        });
        this.root = root;
    }

    _controlHtml(name, icon, label, extra = '') {
        return `<button type="button" class="gh-call__control" data-action="${name}" data-control="${name}" aria-pressed="false">
            <span class="gh-call__disc">${icon}${extra}</span>
            <span class="gh-call__label">${label}</span>
        </button>`;
    }

    _actionHtml(name, discClass, icon, label, iconOnly = false) {
        return `<button type="button" class="gh-call__action" data-action="${name}" data-act="${name}" ${iconOnly ? `aria-label="${label}"` : ''} hidden>
            <span class="gh-call__disc ${discClass}">${icon}</span>
            ${iconOnly ? '' : `<span class="gh-call__label">${label}</span>`}
        </button>`;
    }

    //********************* RENDER ********************************//
    _render() {
        const root = this.root;
        const state = this.state;
        const t = this.t;
        const view = describeCall(state, t);
        this.view = view;

        const name = state.name || t('unknownCaller');
        root.dataset.phase = view.phase;
        root.dataset.halo = view.halo;
        root.dataset.dock = view.dock;
        root.dataset.tone = view.tone;
        root.dataset.direction = state.direction || 'outgoing';
        root.style.setProperty('--call-hue', String(hueOf(name)));
        root.classList.toggle('gh-call--muted', Boolean(state.muted) && view.phase !== 'ended' && view.dock !== 'incoming');
        root.classList.toggle('gh-call--video', Boolean(state.video?.remote) && view.phase !== 'ended');
        root.classList.toggle('gh-call--self-view', Boolean(state.video?.local) && view.phase !== 'ended');

        // Identity
        this._text('.gh-call__name', name);
        const initials = initialsOf(name);
        this._text('.gh-call__initials', initials);
        root.classList.toggle('gh-call--glyph', !initials);
        const photo = root.querySelector('.gh-call__photo');
        const url = state.avatarUrl && state.avatarUrl !== 'undefined' ? state.avatarUrl : '';
        if(url && photo.getAttribute('src') !== url) {
            photo.hidden = false;
            photo.src = url;
        }
        if(!url) photo.hidden = true;
        root.classList.toggle('gh-call--photo', Boolean(url) && !photo.hidden);

        const status = root.querySelector('.gh-call__status');
        if(status.textContent !== view.status) {
            status.textContent = view.status;
            // The timer ticks every second; only a real change of words fades in.
            if(!view.showTimer || !status.classList.contains('gh-call__status--timer')) this._pulse(status, 'gh-call__status--changed');
        }
        status.classList.toggle('gh-call__status--timer', view.showTimer);
        this._text('.gh-call__detail', view.detail);
        root.querySelector('.gh-call__detail').hidden = !view.detail;
        root.setAttribute('aria-label', t('dialogLabel', { name }));

        this._renderNotice(view);
        this._renderControls(view);
        this._renderActions(view);
        this._renderSheet();
        this._tick(view);
        this._scheduleClose(view);
    }

    _renderNotice(view) {
        const state = this.state;
        const notice = this.root.querySelector('.gh-call__notice');
        const live = view.phase !== 'ended' && view.dock !== 'incoming';
        let text = '';
        let kind = '';
        if(live && state.micBlocked) {
            text = this.t('noticeMicBlocked');
            kind = 'error';
        } else if(live && state.muted) {
            text = this.t('noticeMuted');
            kind = 'warn';
        }
        notice.hidden = !text;
        notice.dataset.kind = kind;
        this._text('.gh-call__notice-text', text);
        notice.querySelector('.gh-call__notice-action').hidden = !(kind === 'error' && state.canOpenSettings);
    }

    _renderControls(view) {
        const state = this.state;
        const t = this.t;
        const controls = this.root.querySelector('.gh-call__controls');
        controls.hidden = !view.showControls;

        // Mute
        const mute = controls.querySelector('[data-control="mute"]');
        mute.setAttribute('aria-pressed', String(Boolean(state.muted)));
        mute.setAttribute('aria-label', state.muted ? t('unmute') : t('mute'));
        this._swapIcon(mute, state.muted ? ICONS.micOff : ICONS.mic, state.muted ? 'micOff' : 'mic');
        mute.disabled = Boolean(state.micBlocked);

        // Audio output: hidden where there is nothing to choose (a desktop
        // browser, or an app build without audio routing).
        const audioButton = controls.querySelector('[data-control="audio"]');
        const audio = audioControlOf(state.audio, t);
        audioButton.hidden = !audio;
        if(audio) {
            audioButton.setAttribute('aria-pressed', String(audio.on));
            audioButton.dataset.mode = audio.mode;
            if(audio.mode === 'menu') audioButton.setAttribute('aria-haspopup', 'menu');
            else audioButton.removeAttribute('aria-haspopup');
            audioButton.classList.toggle('gh-call__control--pending', audio.pending);
            this._swapIcon(audioButton, routeIcon(audio.icon === 'headphones' ? 'headset' : audio.icon), audio.icon);
            audioButton.querySelector('.gh-call__label').textContent = audio.label;
        }

        // Camera: a placeholder in the mobile apps for now, the real toggle
        // in a browser.
        const video = controls.querySelector('[data-control="video"]');
        const available = Boolean(state.video?.available);
        const cameraOn = available && Boolean(state.video?.local);
        video.classList.toggle('gh-call__control--soon', !available);
        video.setAttribute('aria-disabled', String(!available));
        video.setAttribute('aria-pressed', String(cameraOn));
        video.setAttribute('aria-label', available ? t('video') : `${t('video')}, ${t('soon')}`);
        this._swapIcon(video, cameraOn ? ICONS.video : ICONS.videoOff, cameraOn ? 'video' : 'videoOff');
        video.hidden = Boolean(state.video?.hidden);

        controls.dataset.count = String([mute, audioButton, video].filter((button) => !button.hidden).length);
    }

    _renderActions(view) {
        const show = new Set();
        if(view.dock === 'incoming') {
            show.add('decline');
            show.add('accept');
        } else if(view.dock === 'call') {
            show.add('hangup');
        } else {
            show.add('close');
            if(view.settings) show.add('settings');
            if(view.retry) show.add('retry');
        }

        this.root.querySelectorAll('.gh-call__action').forEach((button) => {
            const name = button.dataset.act;
            button.hidden = !show.has(name);
            button.disabled = this._busy.has(name);
        });
        this.root.querySelector('.gh-call__actions').dataset.count = String(show.size);
    }

    _renderSheet() {
        const layer = this.root.querySelector('.gh-call__sheet-layer');
        if(layer.hidden) return;
        const audio = this.state.audio;
        if(!audio?.routes?.length || this.view.phase === 'ended') {
            this._closeSheet();
            return;
        }

        const list = layer.querySelector('.gh-call__sheet-list');
        list.innerHTML = '';
        for(const route of audio.routes) {
            const selected = route.id ? route.id === audio.activeId || (!audio.activeId && route.type === audio.route) : route.type === audio.route;
            const option = el('button', 'gh-call__route', {
                type: 'button',
                role: 'menuitemradio',
                'aria-checked': String(selected),
                'data-action': 'route',
                'data-route': route.type,
                'data-route-id': route.id || '',
            });
            option.innerHTML = `<span class="gh-call__route-icon">${routeIcon(route.type)}</span>
                <span class="gh-call__route-name"></span>
                <span class="gh-call__route-check">${selected ? ICONS.check : ''}</span>`;
            option.querySelector('.gh-call__route-name').textContent = routeLabelOf(route, this.t);
            list.appendChild(option);
        }
    }

    _openSheet() {
        const layer = this.root?.querySelector('.gh-call__sheet-layer');
        if(!layer) return;
        layer.hidden = false;
        this._renderSheet();
        requestAnimationFrame(() => layer.classList.add('gh-call__sheet-layer--shown'));
        const checked = layer.querySelector('[aria-checked="true"]') || layer.querySelector('.gh-call__route');
        checked?.focus({ preventScroll: true, focusVisible: false });
    }

    _closeSheet() {
        const layer = this.root?.querySelector('.gh-call__sheet-layer');
        if(!layer || layer.hidden) return;
        layer.classList.remove('gh-call__sheet-layer--shown');
        layer.hidden = true;
        this.root.querySelector('[data-control="audio"]')?.focus({ preventScroll: true, focusVisible: false });
    }

    //********************* TIMER / AUTO CLOSE ********************************//
    _tick(view) {
        if(view.showTimer && !this._timer) {
            this._timer = setInterval(() => this._render(), 1000);
        } else if(!view.showTimer && this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    }

    _scheduleClose(view) {
        if(view.phase !== 'ended' || !view.autoCloseMs || this._closeTimer) return;
        this._closeTimer = setTimeout(() => {
            this._closeTimer = null;
            this.handlers.close?.('timeout');
        }, view.autoCloseMs);
    }

    //********************* EVENTS ********************************//
    _onClick(event) {
        const target = event.target.closest('[data-action]');
        if(!target || !this.root?.contains(target) || target.disabled) return;
        const action = target.dataset.action;
        const handlers = this.handlers;

        switch (action) {
            case 'accept':
            case 'decline':
            case 'hangup':
                // One press per phase: a double tap must not answer twice or
                // hang up the next call.
                this._busy.add(action);
                if(action !== 'hangup') {
                    this._busy.add('accept');
                    this._busy.add('decline');
                }
                this._renderActions(this.view);
                handlers[action]?.();
                break;
            case 'mute':
                handlers.mute?.();
                break;
            case 'audio': {
                const audio = audioControlOf(this.state.audio, this.t);
                if(audio?.mode === 'menu') this._openSheet();
                else handlers.toggleSpeaker?.();
                break;
            }
            case 'route':
                this._closeSheet();
                handlers.selectRoute?.(target.dataset.route, target.dataset.routeId || null);
                break;
            case 'sheet-close':
                this._closeSheet();
                break;
            case 'video':
                if(this.state.video?.available) handlers.camera?.();
                else this.showHint(this.t('hintVideoSoon'));
                break;
            case 'close':
                handlers.close?.('user');
                break;
            case 'retry':
                this._busy.add('retry');
                this._renderActions(this.view);
                handlers.retry?.();
                break;
            case 'settings':
                handlers.openSettings?.();
                break;
            default:
                break;
        }
    }

    _onKeyDown(event) {
        if(!this.root) return;
        if(event.key === 'Escape') {
            const layer = this.root.querySelector('.gh-call__sheet-layer');
            if(!layer.hidden) {
                event.stopPropagation();
                this._closeSheet();
            }
            return;
        }
        if(event.key !== 'Tab') return;

        // Focus stays inside the call screen, as in any modal.
        const focusable = Array.from(this.root.querySelectorAll('button')).filter((button) => !button.disabled && button.offsetParent !== null);
        if(!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if(!this.root.contains(document.activeElement)) {
            event.preventDefault();
            first.focus();
        } else if(event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if(!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    //********************* HELPERS ********************************//
    // Focus moves into the call screen, as into any modal, so a screen
    // reader starts there. On a touch screen it goes to the screen itself:
    // a ring drawn around Accept on a phone nobody has touched reads as a
    // highlight, not as focus. With a keyboard it lands on the main action.
    _focusPrimary() {
        if(!this.root) return;
        const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
        if(touch) {
            if(!this.root.contains(document.activeElement)) this.root.focus({ preventScroll: true });
            return;
        }
        const order = ['accept', 'hangup', 'retry', 'close'];
        for(const name of order) {
            const button = this.root.querySelector(`[data-act="${name}"]`);
            if(button && !button.hidden) {
                button.focus({ preventScroll: true, focusVisible: false });
                return;
            }
        }
    }

    _restoreFocus() {
        const previous = this._previousFocus;
        this._previousFocus = null;
        if(previous && previous.isConnected && typeof previous.focus === 'function') {
            try { previous.focus({ preventScroll: true }); } catch (err) { /* nothing to restore */ }
        }
    }

    _text(selector, text) {
        const node = this.root.querySelector(selector);
        if(node && node.textContent !== text) node.textContent = text || '';
    }

    _swapIcon(button, svg, key) {
        const disc = button.querySelector('.gh-call__disc');
        if(disc.dataset.icon === key) return;
        disc.dataset.icon = key;
        const badge = disc.querySelector('.gh-call__badge');
        disc.innerHTML = svg;
        if(badge) disc.appendChild(badge);
    }

    _pulse(node, className) {
        node.classList.remove(className);
        // Restarts the animation on the same element.
        void node.offsetWidth;
        node.classList.add(className);
    }
}
